import type { Session } from "@supabase/supabase-js";
import { createInviteAcceptanceClient, supabase } from "./supabase";

export type InviteTokenCapture =
  | { kind: "token"; tokenHash: string }
  | { kind: "invalid" }
  | { kind: "none" };

export type VerifiedInviteSession = {
  userId: string;
  session: Session;
};

const inviteTokenPattern = /^[A-Za-z0-9_-]{16,512}$/;

export const MIN_PASSWORD_LENGTH = 6;

export function captureInviteToken(
  location: Pick<Location, "pathname" | "search" | "hash">,
  history: Pick<History, "state" | "replaceState">,
): InviteTokenCapture {
  if (location.pathname !== "/aceitar-convite") return { kind: "none" };

  const fragment = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  history.replaceState(history.state, "", `${location.pathname}${location.search}`);
  if (!fragment) return { kind: "none" };

  const parameters = new URLSearchParams(fragment);
  const tokens = parameters.getAll("token_hash");
  const types = parameters.getAll("type");
  if (
    tokens.length !== 1 ||
    types.length !== 1 ||
    types[0] !== "invite" ||
    !inviteTokenPattern.test(tokens[0] ?? "")
  ) {
    return { kind: "invalid" };
  }

  return { kind: "token", tokenHash: tokens[0] };
}

export function verifiedInviteSessionFromSession(session: Session | null): VerifiedInviteSession | null {
  if (!session?.user?.id || !session.access_token || !session.refresh_token) return null;
  return { userId: session.user.id, session };
}

export async function verifyInviteToken(tokenHash: string): Promise<VerifiedInviteSession> {
  if (!supabase) throw new InviteAcceptanceError("backend_unavailable");

  // verifyOtp estabelece a sessão persistida que o AuthProvider poderá retomar
  // após reload. Nenhuma mutação posterior usa este cliente compartilhado.
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "invite" });
  const verified = verifiedInviteSessionFromSession(data.session);
  if (error || !verified || data.user?.id !== verified.userId) {
    throw new InviteAcceptanceError("invalid_or_expired");
  }
  return verified;
}

export async function completeInvitation(password: string, verified: VerifiedInviteSession): Promise<void> {
  if (password.length < MIN_PASSWORD_LENGTH) throw new InviteAcceptanceError("weak_password");
  const client = createInviteAcceptanceClient();
  if (!client) throw new InviteAcceptanceError("backend_unavailable");

  const { data: restored, error: restoreError } = await client.auth.setSession({
    access_token: verified.session.access_token,
    refresh_token: verified.session.refresh_token,
  });
  if (restoreError || restored.user?.id !== verified.userId || restored.session?.user.id !== verified.userId) {
    throw new InviteAcceptanceError("session_changed");
  }

  await assertBoundClient(client, verified.userId);
  const { error: passwordError } = await client.auth.updateUser({ password });
  if (passwordError) throw new InviteAcceptanceError("password_rejected");

  await assertBoundClient(client, verified.userId);
  const { error: completionError } = await client.rpc("complete_my_invitation");
  if (!completionError) return;

  // A pós-condição é somente leitura e exige accepted_by + perfil active.
  await assertBoundClient(client, verified.userId);
  const { data: confirmed, error: confirmationError } = await client.rpc("my_invitation_completion_is_confirmed");
  if (!confirmationError && confirmed === true) return;
  throw new InviteAcceptanceError("completion_failed");
}

async function assertBoundClient(
  client: NonNullable<ReturnType<typeof createInviteAcceptanceClient>>,
  expectedUserId: string,
): Promise<void> {
  const { data: sessionData, error: sessionError } = await client.auth.getSession();
  const session = sessionData.session;
  if (sessionError || !session || session.user.id !== expectedUserId) {
    throw new InviteAcceptanceError("session_changed");
  }
  const { data: userData, error: userError } = await client.auth.getUser(session.access_token);
  if (userError || userData.user?.id !== expectedUserId) {
    throw new InviteAcceptanceError("session_changed");
  }
}

type InviteAcceptanceErrorCode =
  | "backend_unavailable"
  | "invalid_or_expired"
  | "weak_password"
  | "session_changed"
  | "password_rejected"
  | "completion_failed";

const safeMessages: Record<InviteAcceptanceErrorCode, string> = {
  backend_unavailable: "O serviço de acesso está indisponível. Tente novamente mais tarde.",
  invalid_or_expired: "Este link já foi usado, expirou ou não é válido. Solicite um novo convite ao administrador.",
  weak_password: `Use uma senha com pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`,
  session_changed: "A sessão do convite não pôde ser confirmada. Retome o aceite pela tela inicial.",
  password_rejected: "A senha não pôde ser definida. Use outra senha e tente novamente.",
  completion_failed: "Não foi possível concluir o convite. Tente novamente antes de solicitar outro link.",
};

export class InviteAcceptanceError extends Error {
  constructor(public readonly code: InviteAcceptanceErrorCode) {
    super(safeMessages[code]);
    this.name = "InviteAcceptanceError";
  }
}

export function safeInviteError(error: unknown): string {
  return error instanceof InviteAcceptanceError
    ? error.message
    : "Não foi possível validar o convite. Tente novamente mais tarde.";
}
